import { type ApplianceDragState, type DropIndicatorContent, Kitchen3dController as TdController } from '@rta/rta-blueprint-3d-component';
import { useEffect } from 'react';

import { ContentPanelMode, selectContentPanelMode, useAppSelector } from '@state';
import { useDesign } from './use-design';

// The island-properties panel already caps island width here; a drag grows islands under the same rule
const MAX_ISLAND_WIDTH_IN = 300;

// Badges draw with depth testing off and take the click ahead of the panel behind them, so LIVE goes without
const isNonLive = import.meta.env.VITE_APP_ENVIRONMENT !== 'LIVE';

const applianceName = (controller: TdController, id: string | null): string | null => {
	if (!id) return null;
	const refMap = controller.kitchen?.getReferenceMap(id);
	const { item } = refMap?.panelItem ?? {};
	return item?.description || item?.label || item?.sku || null;
};

/**
 * Turns the 3D view's direct-manipulation features on while it is the visible panel:
 * press-and-hold appliance drag, and the validation badges under the columns they belong to.
 * Both are off for a read-only design, so a press there still orbits the camera, and the
 * badges are off entirely on LIVE.
 */
export const useThreeDInteractions = (): void => {
	const [controller] = useDesign();
	const contentMode = useAppSelector(selectContentPanelMode);
	const readonly = controller instanceof TdController ? controller.readonly : false;

	useEffect(() => {
		if (!(controller instanceof TdController)) return undefined;
		const td = controller;
		const active = contentMode === ContentPanelMode.ThreeD;

		const describeDrop = (state: ApplianceDragState): DropIndicatorContent | null | undefined => {
			if (state.mode !== 'swap') return undefined;
			const name = applianceName(td, state.targetApplianceId);
			return name ? { text: `Swap with ${name}`, icon: 'swap' } : undefined;
		};

		td.setApplianceDragEnabled(active && !readonly, {
			maxIslandWidthIn: MAX_ISLAND_WIDTH_IN,
			assessDrops: true,
			fineTune: true,
			describeDrop
		});
		td.setValidationMarkersEnabled(active && isNonLive);

		return () => {
			td.setApplianceDragEnabled(false);
			td.setValidationMarkersEnabled(false);
		};
	}, [controller, contentMode, readonly]);
};
